// src/pages/Automations/hooks/useAutomations.js
// Custom hook for automation state management and API calls
// Optimized for fast initial load - shows from localStorage immediately

import { useState, useEffect, useCallback, useRef } from 'react';
import { AUTOMATION_CATEGORIES, getAllTriggers } from '../constants/triggers';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Fetch with timeout to prevent hanging
async function fetchWithTimeout(url, options = {}, timeout = 5000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (e) {
    clearTimeout(timeoutId);
    throw e;
  }
}

export function useAutomations() {
  // Initialize from localStorage immediately for instant UI
  const getInitialState = () => {
    try {
      const stored = localStorage.getItem('proirp_automations');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('[useAutomations] Failed to load from storage:', e);
    }
    return { userSettings: {}, templates: {} };
  };

  const initial = getInitialState();
  const [userSettings, setUserSettings] = useState(initial.userSettings);
  const [templates, setTemplates] = useState(initial.templates);
  const [activity, setActivity] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [oepSettings, setOepSettings] = useState({ jan7SendHour: 12, feb1SendHour: 12, mar1SendHour: 12 }); // OEP-specific settings
  const [loading, setLoading] = useState(false); // Start as false - show UI immediately
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [apiLoaded, setApiLoaded] = useState(false);

  const mountedRef = useRef(true);
  const oepOptimisticRef = useRef(false); // Prevents fetchOepSettings from overwriting optimistic updates

  // Save to localStorage
  const saveToStorage = useCallback((settings, tmpl) => {
    try {
      localStorage.setItem('proirp_automations', JSON.stringify({
        userSettings: settings,
        templates: tmpl
      }));
    } catch (e) {
      console.error('[useAutomations] Failed to save to storage:', e);
    }
  }, []);

  // Fetch automation settings from API (with timeout)
  const fetchAutomations = useCallback(async () => {
    try {
      const response = await fetchWithTimeout(`${API_BASE}/api/automations`, {
        credentials: 'include'
      }, 5000);

      if (response.ok) {
        const data = await response.json();
        if (data.ok && data.automations && mountedRef.current) {
          const settings = {};
          data.automations.forEach(a => {
            settings[a.key] = {
              enabled: a.enabled,
              settings: a.settings || {},
              preferred_send_hour: a.preferred_send_hour ?? 9  // Include send hour
            };
          });
          setUserSettings(settings);
          return settings;
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        console.warn('[useAutomations] Fetch automations timed out');
      } else {
        console.error('[useAutomations] Failed to fetch automations:', e);
      }
    }
    return null;
  }, []);

  // Fetch templates from API (with timeout)
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetchWithTimeout(`${API_BASE}/api/automations/templates`, {
        credentials: 'include'
      }, 5000);

      if (response.ok) {
        const data = await response.json();
        if (data.ok && data.templates && mountedRef.current) {
          const grouped = {};
          data.templates.forEach(t => {
            if (t.automation_key) {
              if (!grouped[t.automation_key]) {
                grouped[t.automation_key] = {};
              }
              grouped[t.automation_key][t.type] = t;
            }
          });
          setTemplates(grouped);
          return grouped;
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        console.warn('[useAutomations] Fetch templates timed out');
      } else {
        console.error('[useAutomations] Failed to fetch templates:', e);
      }
    }
    return null;
  }, []);

  // Fetch activity from API (with timeout)
  const fetchActivity = useCallback(async () => {
    try {
      const response = await fetchWithTimeout(`${API_BASE}/api/automations/activity?limit=50`, {
        credentials: 'include'
      }, 5000);

      if (response.ok) {
        const data = await response.json();
        if (data.ok && data.activity && mountedRef.current) {
          setActivity(data.activity);
          return data.activity;
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[useAutomations] Failed to fetch activity:', e);
      }
    }
    return [];
  }, []);

  // Fetch analytics from API (with timeout)
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await fetchWithTimeout(`${API_BASE}/api/automations/analytics`, {
        credentials: 'include'
      }, 5000);

      if (response.ok) {
        const data = await response.json();
        if (data.ok && data.analytics && mountedRef.current) {
          setAnalytics(data.analytics);
          return data.analytics;
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[useAutomations] Failed to fetch analytics:', e);
      }
    }
    return null;
  }, []);

  // Fetch OEP settings (individual send hours per blast)
  const fetchOepSettings = useCallback(async () => {
    console.log(`[OEP] fetchOepSettings called, oepOptimisticRef=${oepOptimisticRef.current}`);
    try {
      const response = await fetchWithTimeout(`${API_BASE}/oep/automations`, {
        credentials: 'include'
      }, 5000);

      if (response.ok) {
        const data = await response.json();
        console.log(`[OEP] fetchOepSettings response:`, data.data);
        // Don't overwrite if there's a pending optimistic update
        if (data.ok && data.data && mountedRef.current && !oepOptimisticRef.current) {
          console.log(`[OEP] fetchOepSettings UPDATING state to:`, {
            jan7SendHour: data.data.jan7SendHour ?? 12,
            feb1SendHour: data.data.feb1SendHour ?? 12,
            mar1SendHour: data.data.mar1SendHour ?? 12
          });
          setOepSettings({
            jan7SendHour: data.data.jan7SendHour ?? 12,
            feb1SendHour: data.data.feb1SendHour ?? 12,
            mar1SendHour: data.data.mar1SendHour ?? 12,
            smsStaggerDelayMs: data.data.smsStaggerDelayMs ?? 1000
          });
          return data.data;
        } else {
          console.log(`[OEP] fetchOepSettings SKIPPED update (optimistic=${oepOptimisticRef.current}, mounted=${mountedRef.current})`);
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[useAutomations] Failed to fetch OEP settings:', e);
      }
    }
    return null;
  }, []);

  // Update OEP send hour for a specific blast (jan7, feb1, or mar1)
  const updateOepSendHour = useCallback(async (blastKey, hour) => {
    console.log(`[OEP] updateOepSendHour called: ${blastKey} -> ${hour}`);
    setSaving(true);
    setError(null);

    // Map blast key to settings key
    const settingsKey = `${blastKey}SendHour`;

    // Set flag to prevent fetchOepSettings from overwriting our update
    oepOptimisticRef.current = true;
    console.log(`[OEP] oepOptimisticRef set to true`);

    // Optimistic update
    const prevSettings = { ...oepSettings };
    console.log(`[OEP] Previous settings:`, prevSettings);
    setOepSettings({ ...prevSettings, [settingsKey]: hour });
    console.log(`[OEP] State updated to:`, { ...prevSettings, [settingsKey]: hour });

    try {
      const response = await fetchWithTimeout(`${API_BASE}/oep/automations`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [settingsKey]: hour })
      }, 5000);

      if (!response.ok) {
        throw new Error('Failed to update OEP send hour');
      }
      // Success - keep the optimistic update, clear flag after a delay
      // (delay allows any in-flight fetches to complete without overwriting)
      setTimeout(() => { oepOptimisticRef.current = false; }, 2000);
    } catch (e) {
      console.error('[useAutomations] Update OEP send hour failed:', e);
      setOepSettings(prevSettings);
      setError('Failed to update send time');
      oepOptimisticRef.current = false; // Clear flag on error so refetch can work
    } finally {
      setSaving(false);
    }
  }, [oepSettings]);

  // Update automation send hour (for non-OEP automations)
  const updateAutomationSendHour = useCallback(async (automationKey, hour) => {
    setSaving(true);
    setError(null);

    try {
      const response = await fetchWithTimeout(`${API_BASE}/api/automations/${automationKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ preferredSendHour: hour })
      }, 5000);

      if (!response.ok) {
        throw new Error('Failed to update send hour');
      }

      // Refresh automations to get updated data
      await fetchAutomations();
    } catch (e) {
      console.error('[useAutomations] Update automation send hour failed:', e);
      setError('Failed to update send time');
    } finally {
      setSaving(false);
    }
  }, [fetchAutomations]);

  // Toggle automation enabled/disabled
  const toggleAutomation = useCallback(async (triggerKey, enabled) => {
    setSaving(true);
    setError(null);

    // Optimistic update
    const prevSettings = { ...userSettings };
    const newSettings = {
      ...prevSettings,
      [triggerKey]: { ...(prevSettings[triggerKey] || {}), enabled }
    };
    setUserSettings(newSettings);
    saveToStorage(newSettings, templates);

    try {
      const response = await fetchWithTimeout(`${API_BASE}/api/automations/${triggerKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled })
      }, 5000);

      if (!response.ok) {
        throw new Error('Failed to update automation');
      }
    } catch (e) {
      console.error('[useAutomations] Toggle failed:', e);
      setUserSettings(prevSettings);
      saveToStorage(prevSettings, templates);
      setError('Failed to update automation');
    } finally {
      setSaving(false);
    }
  }, [userSettings, templates, saveToStorage]);

  // Update template
  const updateTemplate = useCallback(async (templateId, updates) => {
    setSaving(true);
    setError(null);

    try {
      const response = await fetchWithTimeout(`${API_BASE}/api/automations/templates/${templateId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      }, 5000);

      if (!response.ok) {
        throw new Error('Failed to update template');
      }

      const data = await response.json();
      await fetchTemplates();
      return data.template;
    } catch (e) {
      console.error('[useAutomations] Update template failed:', e);
      setError('Failed to update template');
      throw e;
    } finally {
      setSaving(false);
    }
  }, [fetchTemplates]);

  // Create new template
  const createTemplate = useCallback(async (template) => {
    setSaving(true);
    setError(null);

    try {
      const response = await fetchWithTimeout(`${API_BASE}/api/automations/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(template)
      }, 5000);

      if (!response.ok) {
        throw new Error('Failed to create template');
      }

      const data = await response.json();
      await fetchTemplates();
      return data.template;
    } catch (e) {
      console.error('[useAutomations] Create template failed:', e);
      setError('Failed to create template');
      throw e;
    } finally {
      setSaving(false);
    }
  }, [fetchTemplates]);

  // Resend failed automation
  const resendAutomation = useCallback(async (activityId) => {
    try {
      const response = await fetchWithTimeout(`${API_BASE}/api/automations/activity/${activityId}/resend`, {
        method: 'POST',
        credentials: 'include'
      }, 5000);

      if (!response.ok) {
        throw new Error('Failed to resend');
      }

      await fetchActivity();
      return true;
    } catch (e) {
      console.error('[useAutomations] Resend failed:', e);
      setError('Failed to resend automation');
      return false;
    }
  }, [fetchActivity]);

  // Check if a trigger is enabled
  const isEnabled = useCallback((triggerKey) => {
    if (userSettings[triggerKey] !== undefined) {
      return userSettings[triggerKey].enabled;
    }
    // Fall back to default from trigger definition
    const trigger = getAllTriggers().find(t => t.key === triggerKey);
    return trigger?.defaultEnabled || false;
  }, [userSettings]);

  // Get enabled count for a category
  const getEnabledCount = useCallback((categoryKey) => {
    const category = AUTOMATION_CATEGORIES.find(c => c.key === categoryKey);
    if (!category) return 0;
    return category.triggers.filter(t => isEnabled(t.key)).length;
  }, [isEnabled]);

  // Get total enabled count
  const getTotalEnabled = useCallback(() => {
    return getAllTriggers().filter(t => isEnabled(t.key)).length;
  }, [isEnabled]);

  // Background data fetch - doesn't block UI
  useEffect(() => {
    mountedRef.current = true;

    // Fetch API data in background (don't block UI)
    const loadApiData = async () => {
      try {
        // Fetch automations, templates, and OEP settings in parallel
        // OEP settings MUST be awaited to prevent race condition where background
        // fetch overwrites user's selection (fixes send hour reverting to noon bug)
        const [settings, tmpl] = await Promise.all([
          fetchAutomations(),
          fetchTemplates(),
          fetchOepSettings()  // Moved here - must complete before user can interact
        ]);

        if (mountedRef.current) {
          // Update storage with fresh data
          if (settings || tmpl) {
            saveToStorage(settings || userSettings, tmpl || templates);
          }
          setApiLoaded(true);
        }

        // Fetch activity and analytics in background (lowest priority, no user interaction)
        fetchActivity();
        fetchAnalytics();
      } catch (e) {
        console.error('[useAutomations] Background load failed:', e);
      }
    };

    loadApiData();

    return () => {
      mountedRef.current = false;
    };
  }, []); // Only run once on mount

  return {
    // Data
    categories: AUTOMATION_CATEGORIES,
    userSettings,
    templates,
    activity,
    analytics,
    oepSettings, // OEP-specific settings (preferredSendHour, etc.)

    // State
    loading,
    saving,
    error,
    apiLoaded, // New: indicates if API data has been fetched

    // Actions
    toggleAutomation,
    updateTemplate,
    createTemplate,
    resendAutomation,
    updateOepSendHour, // Update OEP send hour for specific blast
    updateAutomationSendHour, // Update send hour for non-OEP automations

    // Helpers
    isEnabled,
    getEnabledCount,
    getTotalEnabled,

    // Refresh
    refetch: async () => {
      await Promise.all([fetchAutomations(), fetchTemplates(), fetchActivity(), fetchAnalytics(), fetchOepSettings()]);
    }
  };
}

export default useAutomations;
